import Sentry from "@/integrations/Sentry";
import type { FileRecord } from "@cohesive/api-client";
import { useQuery } from "@tanstack/react-query";

/**
 * Matches `https://file.cohesive.ai/public/{id}` and any environment
 * subdomain variant (`file.local.cohesive.ai`, `file.dev.cohesive.ai`, …).
 * The optional middle segment is restricted to a single label of
 * lowercase alphanumerics + hyphens to avoid accidentally matching
 * unrelated hosts that happen to contain `cohesive.ai`.
 */
export const PUBLIC_URL_REGEX =
  /https:\/\/file\.(?:[a-z0-9-]+\.)?cohesive\.ai\/public\/([A-Za-z0-9_-]+)/g;

/** A Cohesive public-file reference scraped from an HTML body. */
export interface ReferencedFile {
  /** FileRecord id from the `/public/{id}` URL. */
  id: string;
  /** The full original public URL exactly as it appears in the HTML. */
  url: string;
}

/**
 * Extract the distinct Cohesive public-file references in `html`,
 * keeping the full URL alongside each id. Callers that can't fetch the
 * FileRecord itself (e.g. it belongs to another account — onboarding
 * decks embed shared template assets the viewing user doesn't own) fall
 * back to linking the raw public URL.
 */
export function extractReferencedFiles(html: string): ReferencedFile[] {
  const byId = new Map<string, string>();
  for (const match of html.matchAll(PUBLIC_URL_REGEX)) {
    const id = match[1];
    if (id && !byId.has(id)) byId.set(id, match[0]);
  }
  return Array.from(byId, ([id, url]) => ({ id, url }));
}

export interface UseHtmlReferencedIdsOptions {
  /**
   * When false, the query is disabled and returns no data. Used by
   * `FilePublishButton` to skip the body fetch entirely for non-HTML kinds.
   * Defaults to true.
   */
  enabled?: boolean;
}

/**
 * Fetches the rendered HTML body and returns the referenced
 * `file.{env}.cohesive.ai/public/{id}` files found in it (each as an
 * `{ id, url }` pair, plus an `referencedIds` convenience list). Backed
 * by react-query so multiple consumers (iframe renderer + publish
 * dialog) share a single fetch per record URL.
 *
 * Failures are reported to Sentry but resolved as an empty list — the
 * publish UI degrades gracefully (no dependency rows) rather than
 * blocking the user.
 */
export function useHtmlReferencedIds(
  record: Pick<FileRecord, "id" | "url" | "content_hash">,
  options: UseHtmlReferencedIdsOptions = {},
) {
  const { enabled = true } = options;

  // `record.url` is a presigned URL whose auth query params rotate on every
  // FileRecord re-fetch. Keying the query on the full URL would make every
  // rotation a brand-new cache entry — refetching the body (and cascading
  // into a re-presign + iframe reload) even though the content is identical.
  // Key on the stable S3 path instead, plus `content_hash` so genuine content
  // changes (in-place overwrites at the same key) still trigger a refetch.
  //
  // NB: do NOT key on `updated_at` — it bumps for non-content reasons (and is
  // churned by `file_record.updated` WebSocket events while the pane is open),
  // which would refetch the body on every event. `content_hash` changes if
  // and only if the file content changes.
  const urlKey = (() => {
    if (!record.url) return null;
    try {
      return new URL(record.url).pathname;
    } catch {
      return record.url;
    }
  })();

  return useQuery({
    queryKey: ["html-referenced-ids", record.id, urlKey, record.content_hash],
    enabled: enabled && !!record.url,
    staleTime: 5 * 60 * 1000,
    // Short-lived on purpose: a whole HTML body is far too big for the
    // localStorage snapshot, and every `referencedFiles[].url` would be
    // redacted on write anyway — forcing a refetch on restore for no gain.
    // Opting out keeps the `gcTime` below the persist layer's `maxAge` from
    // silently rewriting the snapshot when this entry is collected.
    gcTime: 10 * 60 * 1000,
    meta: { persist: false },
    retry: false,
    queryFn: async () => {
      try {
        const res = await fetch(record.url!);
        if (!res.ok) {
          throw new Error(`HTML body fetch failed (${res.status})`);
        }
        const body = await res.text();
        const referencedFiles = extractReferencedFiles(body);
        return {
          body,
          referencedFiles,
          referencedIds: referencedFiles.map((f) => f.id),
        };
      } catch (err) {
        Sentry.captureException(err, {
          tags: { feature: "html-publish-rewrite" },
          extra: {
            fileRecordId: record.id,
            stage: "fetch-html-body",
          },
        });
        throw err;
      }
    },
  });
}
